import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useKindList } from '@/api/queries/catalog-queries'
import { DetailField, InspectionDetailsSection } from '@/components/commons'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import { Button } from '@/components/ui/button'
import { Field, FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import { useVereAgirForm } from '@/hooks/forms/use-vere-agir-form'
import type { VereAgirFormData } from '@/schemas/ver-e-agir-schema'
import type {
	InspectionStatus,
	Potential as PotentialValue,
	VereAgirOutput,
	VereAgirSituation,
	VereAgirUpdateInput,
} from '@/types'
import type { OrgCR } from '@/types/organization'
import { capitalize } from '@/utils/capitalize'
import {
	formatDateToDisplay,
	formatDateYMD,
	parseStringToDateForDisplay,
} from '@/utils/formatDate'
import { convertHeicIfNeeded } from '@/utils/image-compression'
import {
	INSPECTION_COLORS,
	InspectionBeforeAfterPhotos,
	InspectionDateField,
	InspectionDetailsHeader,
	InspectionPhotoField,
} from '../inspections'
import { Spinner } from '../ui/spinner'

interface PersonEdit {
	id: string
	name: string
	badge: string
}

export interface CorrectSeeActFormProps {
	initial?: {
		id: 'insp-001'
		type: 'yellow-card'
		date: '01/01/2025'
		leaderId: '0a4545f4-dbcb-465c-94fa-dca128e379e2' // Ana Segurança
		branchId: '1'
		crId: '0ff2cd66-7479-4d30-97dc-0751898a7d32'
		potential: 'n1'
		issuerId: '9d4f9f4c-9d52-4f88-8c57-0c9c8c5a9999' // Camila Oliveira
		typologyId: '1'
		riskId: '1-1'
		unsafeDescription: 'Trabalhador operando máquina sem proteção adequada'
		safeDescription: 'Instalar proteção de segurança na máquina e treinar operador'
		photoUrl: 'https://example.com/photo.jpg'
	}
	seeActSelected?: VereAgirOutput
	persons: PersonEdit[]
	crList?: OrgCR[]
	isLoading?: boolean
	isDisabled?: boolean
	statusOnly?: boolean
	onSave: (data: VereAgirUpdateInput) => void
	onClose: () => void
}

export function CorrectSeeActFormContent({
	initial,
	seeActSelected,
	persons,
	crList,
	isLoading = false,
	isDisabled = false,
	statusOnly = false,
	onSave,
	onClose,
}: CorrectSeeActFormProps) {
	const { t } = useTranslation()
	const form = useVereAgirForm()
	const {
		setFocus,
		formState: { errors },
	} = form
	const { handleError } = useFormErrorFocus(errors, setFocus)

	const kindId = form.watch('kindId')
	const image = form.watch('image')
	const status = form.watch('status')

	const [photoPreview, setPhotoPreview] = useState<string | null>(
		seeActSelected?.imagePath
			? seeActSelected.imagePath
			: (initial?.photoUrl ?? null),
	)
	const [removeImage, setRemoveImage] = useState(false)
	const [resolutionImage, setResolutionImage] = useState<File | null>(null)
	const [resolutionPreview, setResolutionPreview] = useState<string | null>(
		seeActSelected?.resolutionImagePath ?? null,
	)
	const [removeResolutionImage, setRemoveResolutionImage] = useState(false)
	const [selectedCrId, setSelectedCrId] = useState<string>(
		seeActSelected?.cr.id ?? '',
	)

	const { data: typologyData } = useKindList()
	const selectedKind = typologyData?.items.find((k) => k.id === kindId)

	const colors = INSPECTION_COLORS.sunnyYellow
	const statusOnlyDate = seeActSelected?.date ?? initial?.date

	useEffect(() => {
		const parsedDate = parseStringToDateForDisplay(
			seeActSelected?.date.toString() ?? initial?.date ?? '',
		)
		const parsedDueDate = parseStringToDateForDisplay(
			seeActSelected?.dueDate ?? '',
		)
		const initialPotential = (
			seeActSelected?.potential ??
			initial?.potential ??
			'n5'
		).toLowerCase() as PotentialValue

		form.reset({
			date: parsedDate ? formatDateYMD(parsedDate) : '',
			kindId: seeActSelected?.kind.id ?? initial?.typologyId ?? '',
			riskId: seeActSelected?.risk.id ?? initial?.riskId ?? '',
			potential: initialPotential,
			description:
				seeActSelected?.description ?? initial?.safeDescription ?? '',
			immediateAction: seeActSelected?.immediateAction?.trim() ?? '',
			responsibleId: seeActSelected?.responsible.id ?? '',
			dueDate: parsedDueDate ? formatDateYMD(parsedDueDate) : '',
			status: seeActSelected?.status ?? 'IN_PROGRESS',
			situation: seeActSelected?.situation ?? 'STOP_AND_FIX',
			image: undefined,
		})

		setPhotoPreview(
			seeActSelected?.imagePath
				? seeActSelected.imagePath
				: (initial?.photoUrl ?? null),
		)
		setRemoveImage(false)
		setResolutionImage(null)
		setResolutionPreview(seeActSelected?.resolutionImagePath ?? null)
		setRemoveResolutionImage(false)
		setSelectedCrId(seeActSelected?.cr.id ?? '')
	}, [form, initial, seeActSelected])

	// Reset risk when typology changes
	const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		form.setValue('image', file, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setRemoveImage(false)
		const url = URL.createObjectURL(file)
		setPhotoPreview(url)
	}

	const handlePhotoRemove = () => {
		if (photoPreview?.startsWith('blob:')) {
			URL.revokeObjectURL(photoPreview)
		}
		form.setValue('image', undefined, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setPhotoPreview(null)
		setRemoveImage(true)
	}

	const handleResolutionPhotoChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		if (resolutionPreview?.startsWith('blob:')) {
			URL.revokeObjectURL(resolutionPreview)
		}
		setResolutionImage(file)
		setRemoveResolutionImage(false)
		setResolutionPreview(URL.createObjectURL(file))
	}

	const handleResolutionPhotoRemove = () => {
		if (resolutionPreview?.startsWith('blob:')) {
			URL.revokeObjectURL(resolutionPreview)
		}
		setResolutionImage(null)
		setResolutionPreview(null)
		setRemoveResolutionImage(true)
	}

	const handleSave = (formData: VereAgirFormData) => {
		if (
			!formData.date ||
			!formData.kindId ||
			!formData.riskId ||
			!formData.responsibleId ||
			!formData.dueDate ||
			!formData.status ||
			!formData.situation ||
			!formData.description ||
			!formData.immediateAction
		) {
			toast.error(t('inspections.seeAndAct.validationError'))
			return
		}

		// Envia apenas os campos que o usuario alterou (diff contra o form.reset inicial)
		const { dirtyFields } = form.formState
		const payload: VereAgirUpdateInput = {}

		if (selectedCrId && selectedCrId !== (seeActSelected?.cr.id ?? '')) {
			payload.crId = selectedCrId
		}
		if (dirtyFields.date) {
			payload.date = formData.date
		}
		if (dirtyFields.kindId) {
			payload.kindId = formData.kindId
		}
		if (dirtyFields.riskId) {
			payload.riskId = formData.riskId
		}
		if (dirtyFields.potential) {
			payload.potential = formData.potential as PotentialValue
		}
		if (dirtyFields.description) {
			payload.description = formData.description
		}
		if (dirtyFields.immediateAction) {
			payload.immediateAction = formData.immediateAction?.trim() ?? ''
		}
		if (dirtyFields.responsibleId) {
			payload.responsibleId = formData.responsibleId
		}
		if (dirtyFields.dueDate) {
			payload.dueDate = formData.dueDate
		}
		if (dirtyFields.status) {
			payload.status = formData.status as InspectionStatus
		}
		if (dirtyFields.situation) {
			payload.situation = formData.situation as VereAgirSituation
		}
		if (image) {
			payload.image = image
		}
		if (removeImage && seeActSelected?.imagePath) {
			payload.removeImage = true
		}
		if (formData.status === 'RESOLVED') {
			if (resolutionImage) {
				payload.resolutionImage = resolutionImage
			}
			if (removeResolutionImage && seeActSelected?.resolutionImagePath) {
				payload.removeResolutionImage = true
			}
		}

		if (Object.keys(payload).length === 0) {
			onClose()
			return
		}

		onSave(payload)
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSave, handleError)}
				className="flex flex-col gap-5 px-1"
			>
				{statusOnly && (
					<>
						<div className="space-y-5 py-1">
							<InspectionDetailsHeader
								date={
									statusOnlyDate
										? formatDateToDisplay(String(statusOnlyDate))
										: '-'
								}
								createdBy={
									seeActSelected?.createdBy?.name
										? capitalize(seeActSelected.createdBy.name)
										: undefined
								}
							/>

							{seeActSelected?.customer?.id && (
								<InspectionDetailsSection>
									<DetailField
										label={t('inspections.common.customer')}
										value={capitalize(seeActSelected.customer.name)}
									/>
								</InspectionDetailsSection>
							)}

							<InspectionDetailsSection>
								<DetailField
									label={t('inspections.seeAndAct.typology')}
									value={seeActSelected?.kind.name ?? '-'}
								/>
								<DetailField
									label={t('inspections.seeAndAct.risk')}
									value={seeActSelected?.risk.name ?? '-'}
								/>
							</InspectionDetailsSection>

							<InspectionDetailsSection layout="vertical">
								<DetailField
									label={t('inspections.seeAndAct.description')}
									value={seeActSelected?.description ?? '-'}
								/>
							</InspectionDetailsSection>

							<InspectionDetailsSection layout="vertical">
								<DetailField
									label={t('inspections.seeAndAct.immediateAction')}
									value={seeActSelected?.immediateAction ?? '-'}
								/>
							</InspectionDetailsSection>

							<InspectionDetailsSection showSeparator={!!photoPreview}>
								<DetailField
									label={t('inspections.seeAndAct.responsible')}
									value={
										seeActSelected?.responsible?.name
											? capitalize(seeActSelected.responsible.name)
											: '-'
									}
								/>
								<DetailField
									label={t('inspections.seeAndAct.deadline')}
									value={
										seeActSelected?.dueDate
											? formatDateToDisplay(seeActSelected.dueDate)
											: '-'
									}
								/>
							</InspectionDetailsSection>

							<InspectionBeforeAfterPhotos
								beforeSrc={photoPreview}
								beforeLabel={t('inspections.seeAndAct.photoBefore')}
								afterLabel={t('inspections.seeAndAct.photoAfter')}
								colors={colors}
							/>
						</div>

						<Separator className="bg-border/60" />
					</>
				)}

				{!statusOnly && (
					<>
						{crList && crList.length > 0 && (
							<FormItem
								label={t('inspections.common.contract')}
								isRequired={false}
								error=""
							>
								<FieldContent fieldName="crId">
									<SearchableSelect
										id="see-act-edit-cr"
										nameField="crId"
										value={selectedCrId}
										onValueChange={setSelectedCrId}
										options={crList.map((cr) => ({
											value: cr.id,
											label: cr.name,
										}))}
										placeholder={t('inspections.common.contract')}
										searchPlaceholder={t('common.search')}
									/>
								</FieldContent>
							</FormItem>
						)}

						<FormField
							name="date"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.common.date')}
									error={form.formState.errors.date?.message}
								>
									<InspectionDateField
										value={
											field.value
												? parseStringToDateForDisplay(field.value)
												: undefined
										}
										onChange={(date) =>
											field.onChange(date ? formatDateYMD(date) : '')
										}
									/>
								</FormItem>
							)}
						/>

						<FormField
							name="responsibleId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.responsible')}
									error={form.formState.errors.responsibleId?.message}
								>
									<FieldContent fieldName={field.name}>
										<SearchableSelect
											id="inspection-responsible"
											ref={field.ref}
											nameField={field.name}
											value={field.value}
											onValueChange={field.onChange}
											options={persons.map((person) => ({
												value: person.id,
												label: `${person.name}`,
											}))}
											placeholder={t('inspections.seeAndAct.selectResponsible')}
											searchPlaceholder={t('common.search')}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="dueDate"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.deadline')}
									error={form.formState.errors.dueDate?.message}
								>
									<InspectionDateField
										value={
											field.value
												? parseStringToDateForDisplay(field.value)
												: undefined
										}
										onChange={(date) =>
											field.onChange(date ? formatDateYMD(date) : '')
										}
										isDueDate={true}
									/>
								</FormItem>
							)}
						/>
					</>
				)}

				<FormField
					name="status"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('inspections.seeAndAct.status')}
							error={form.formState.errors.status?.message}
						>
							<FieldContent fieldName={field.name}>
								<NativeSelect
									id="inspection-status"
									name={field.name}
									ref={field.ref}
									value={field.value}
									onChange={field.onChange}
								>
									<NativeSelectOption value="" disabled>
										{t('inspections.seeAndAct.status')}
									</NativeSelectOption>
									{statusOnly && field.value === 'IN_PROGRESS' && (
										<NativeSelectOption value="IN_PROGRESS">
											{t('inspections.seeAndAct.statusInProgress')}
										</NativeSelectOption>
									)}
									{!statusOnly && (
										<NativeSelectOption value="IN_PROGRESS">
											{t('inspections.seeAndAct.statusInProgress')}
										</NativeSelectOption>
									)}
									<NativeSelectOption value="RESOLVED">
										{t('inspections.seeAndAct.statusResolved')}
									</NativeSelectOption>
								</NativeSelect>
							</FieldContent>
						</FormItem>
					)}
				/>

				{status === 'RESOLVED' && (
					<FormItem
						label={t('inspections.seeAndAct.photoAfter')}
						isRequired={false}
						error=""
					>
						<InspectionPhotoField
							attachLabel={t('inspections.seeAndAct.attachResolutionPhoto')}
							altText={t('inspections.seeAndAct.photoAfter')}
							preview={resolutionPreview}
							onFileChange={handleResolutionPhotoChange}
							onRemove={handleResolutionPhotoRemove}
							colors={colors}
						/>
					</FormItem>
				)}

				{!statusOnly && (
					<>
						<FormField
							name="situation"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.situation')}
									error={form.formState.errors.situation?.message}
								>
									<FieldContent fieldName={field.name}>
										<NativeSelect
											id="inspection-situation"
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
										>
											<NativeSelectOption value="" disabled>
												{t('inspections.seeAndAct.situation')}
											</NativeSelectOption>
											<NativeSelectOption value="STOP_AND_FIX">
												{t('inspections.seeAndAct.situationStopAndFix')}
											</NativeSelectOption>
											<NativeSelectOption value="FIX_AND_CONTINUE">
												{t('inspections.seeAndAct.situationFixAndContinue')}
											</NativeSelectOption>
										</NativeSelect>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="kindId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.typology')}
									error={form.formState.errors.kindId?.message}
								>
									<FieldContent fieldName={field.name}>
										<NativeSelect
											id="inspection-typology"
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={(e) => {
												field.onChange(e)
												form.setValue('riskId', '', {
													shouldDirty: true,
													shouldValidate: true,
												})
											}}
										>
											<NativeSelectOption value="" disabled>
												{t('inspections.seeAndAct.selectTypology')}
											</NativeSelectOption>
											{typologyData?.items.map((typology) => (
												<NativeSelectOption
													key={typology.id}
													value={typology.id}
												>
													{typology.name}
												</NativeSelectOption>
											))}
										</NativeSelect>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="riskId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.risk')}
									error={form.formState.errors.riskId?.message}
								>
									<Field>
										<FieldContent fieldName={field.name}>
											<NativeSelect
												id="inspection-risk"
												name={field.name}
												ref={field.ref}
												value={field.value}
												onChange={field.onChange}
												disabled={!kindId || !selectedKind}
											>
												<NativeSelectOption value="" disabled>
													{t('inspections.seeAndAct.selectRisk')}
												</NativeSelectOption>
												{selectedKind?.risks.map((risk) => (
													<NativeSelectOption key={risk.id} value={risk.id}>
														{risk.name}
													</NativeSelectOption>
												))}
											</NativeSelect>
										</FieldContent>
									</Field>
								</FormItem>
							)}
						/>

						<Separator className="bg-border/60" />

						<FormField
							name="description"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.description')}
									error={form.formState.errors.description?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											id="seeact-description"
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t('inspections.seeAndAct.description')}
											rows={3}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="immediateAction"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.immediateAction')}
									error={form.formState.errors.immediateAction?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											id="seeact-immediate-action"
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t(
												'inspections.seeAndAct.immediateActionPlaceholder',
											)}
											rows={3}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="image"
							control={form.control}
							render={() => (
								<FormItem
									label={t('inspections.seeAndAct.photo')}
									isRequired={false}
									error=""
								>
									<InspectionPhotoField
										attachLabel={t('inspections.seeAndAct.attachPhoto')}
										altText={t('inspections.seeAndAct.photo')}
										preview={photoPreview}
										onFileChange={handlePhotoChange}
										onRemove={handlePhotoRemove}
										colors={colors}
									/>
								</FormItem>
							)}
						/>
					</>
				)}

				<div className="flex gap-3 pt-1">
					{isLoading ? (
						<Button
							disabled={isLoading}
							type="button"
							className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
						>
							<Spinner />
							{t('inspections.seeAndAct.saving')}
						</Button>
					) : (
						<>
							<Button
								type="button"
								variant="cancel"
								onClick={onClose}
								className="flex-1"
							>
								{t('inspections.seeAndAct.cancel')}
							</Button>
							<Button
								disabled={isLoading || isDisabled}
								type="submit"
								className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t('inspections.seeAndAct.save')}
							</Button>
						</>
					)}
				</div>
			</form>
		</Form>
	)
}
